import { Grid } from '@mui/material';
import clsx from "clsx";
import React, { CSSProperties, useContext, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import PersonIcon from '@mui/icons-material/Person';
import BugReportIcon from '@mui/icons-material/BugReport';
import styles from "../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import globalStyles from "../../../assets/styles/global.module.scss";
import IconsColor from '../../../components/utility/iconsColor';
import GlobalTooltip from '../../../components/utility/tooltip/globalTooltip';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { currentActiveThreadChatbotThreadData, getSpecificChatbotThreadData, reactionData } from '../../../models/axiosModel';
import { formatDateAccordingToTimezoneUsingMoment, isAdmin } from '../../../utils/constants';
import { renderBotMsg, getMessageStatusIcons, renderUserMsg, renderThreadMsg } from '../renderUtils';
import { getDynamicColorsBySource, getReleventWhatsappStatus, getUserMessageCopyText, stripMarkdown } from '../utils';
import ChatLogsTemplatePreview from '../../../pages/ruleBasedInbox/threadView/components/chatbotActionLogView/chatLogsTemplatePreview';
import CarouselTemplatePreviewInbox from '../../../pages/ruleBasedInbox/threadView/components/chatbotActionLogView/carouselTemplatePreviewInbox';
import { useRuleBasedInboxStore } from '../../../store/ruleBasedInboxStore';
import { useShallow } from 'zustand/react/shallow';
import { ACTION_TYPE_NAME } from '../../../components/dashboard/flowBuilder/flowBuilderConstants';

interface UserMessageProps {
    thread: currentActiveThreadChatbotThreadData;
    aiMessageExecutionActionType: boolean;
    ind: number;
    targetRef: React.RefObject<HTMLElement | null>;
    getQuotedThread: (thread: currentActiveThreadChatbotThreadData) => currentActiveThreadChatbotThreadData | undefined;
    getDynamicColorsByChatbotRouteSource: (source?: string) => ReturnType<typeof getDynamicColorsBySource>;
    getSteps: (ai_execution_id: string) => void | Promise<void>;
    getSubChatbotExceutionLog: (ai_execution_id: string) => void | Promise<void>;
    handleExecutionTimeLog: (thread: currentActiveThreadChatbotThreadData) => void;
}

const UserMessage = (props: UserMessageProps) => {

    const { thread, aiMessageExecutionActionType, ind, targetRef, getQuotedThread, getDynamicColorsByChatbotRouteSource,
        getSteps, getSubChatbotExceutionLog, handleExecutionTimeLog
    } = props;

    
    const { userDetail } = useContext(UserContext) as UserContextPayload;
    const { currentActiveChatbotThread, flagDebug } = useRuleBasedInboxStore(
        useShallow((state) => ({
            currentActiveChatbotThread: state.currentActiveChatbotThread,
            flagDebug: state.flagDebug,
        }))
    );
    
    const [copyText, setCopyText] = useState<string>("Copy");

    const { user_msg_bg_color, user_msg_color } = getDynamicColorsByChatbotRouteSource();
    const quotedThread = getQuotedThread(thread as currentActiveThreadChatbotThreadData);
    const quotedAiThread = currentActiveChatbotThread?.chatbot_thread_data?.find(
        (initialThread: getSpecificChatbotThreadData | currentActiveThreadChatbotThreadData) =>
            initialThread.chatbot_thread_id == (thread as any)?.parent_thread_execution_log_id
    ) as any;
    const actionExecutionData = (thread as any)?.action_execution_data;
    const reactionList = actionExecutionData?.reaction_data;
    // null for messages without text content, which hides the copy icon
    const copyMessageText = getUserMessageCopyText(thread);

    const renderQuoteReply = () => {
        if (!thread?.parent_thread_execution_log_id) return null;

        return (
            <div
                className={clsx(styles.quoteReplyDiv, styles.staticWorkflowChatQuoteReplyDiv)}
            >
                {!aiMessageExecutionActionType ?
                    <>
                        <div
                            className={clsx(globalStyles.primaryFontWeight, globalStyles.primaryColor)}
                        >
                            {(quotedThread && quotedThread?.flag_user_message) ?
                                "You" :
                                currentActiveChatbotThread?.chatbot_name ?? ""
                            }
                        </div>
                        {renderBotMsg(quotedThread?.chatbot_thread_action_type ?? '',
                            quotedThread?.chatbot_action_execution_data,
                            ind, targetRef, thread)
                        }
                        {quotedThread?.action_type_name?.toLowerCase() == ACTION_TYPE_NAME['CAROUSEL_TEMPLATE']?.toLowerCase() &&
                            <CarouselTemplatePreviewInbox
                                actionData={quotedThread?.chatbot_action_execution_data}
                                justifyContent="start"
                            />
                        }
                    </>
                    :
                    <>
                        <div
                            className={clsx(globalStyles.primaryFontWeight, globalStyles.primaryColor)}
                        >
                            {quotedAiThread?.flag_user_message ? "You" : currentActiveChatbotThread?.chatbot_name ?? ""}
                        </div>

                        {quotedAiThread?.action_execution_data?.thread_template_data != null ?
                            <ChatLogsTemplatePreview actionData={quotedAiThread?.action_execution_data?.thread_template_data?.action_execution_data} key={ind} ref={targetRef} />
                            :
                            renderThreadMsg(quotedAiThread?.action_execution_data,
                                "messageReply",
                            )
                        }
                    </>
                }
            </div>
        );
    };

    return (
        <Grid item className={styles.userMessageGrid}>
            <Grid container className={styles.userMessageGridContainer}>

                <Grid item className={styles.chatbotChatIconDiv} >
                    <PersonIcon />
                </Grid>

                {isAdmin(userDetail) && aiMessageExecutionActionType &&
                    <Grid item className={styles.relativePositonStyle}>
                        <GlobalTooltip
                            title="Get AI Steps"
                            placement='top'
                            arrow
                        >
                            <span className={styles.openAILogoStyle} onClick={() => { getSteps(thread?.action_execution_data?.ai_execution_id ?? '') }}>
                                <IconsColor iconName="AIStepsDataIcon" height="20" width="20" />
                            </span>
                        </GlobalTooltip>
                    </Grid>
                }

                <Grid item
                    className={styles.userMessageInnerGrid}
                >
                    <Grid container>
                        <Grid item
                            direction={"column"}
                            className={styles.userMessageInnerGridItem}
                        >
                            <Grid container>
                                <Grid item
                                    className={styles.parentbotMsgBoxStyle}
                                    sx={{ backgroundColor: user_msg_bg_color }}
                                >
                                    {(isAdmin(userDetail) && aiMessageExecutionActionType) &&
                                        <Grid item className={styles.relativePositonStyle}>
                                            <GlobalTooltip
                                                title="Sub Chatbot Execution Logs"
                                                placement='top'
                                                arrow
                                            >
                                                <span onClick={() => { getSubChatbotExceutionLog(thread?.action_execution_data?.ai_execution_id ?? '') }} className={styles.executionLogButtonStyle}>
                                                    <IconsColor iconName="subChatbotIcon" height="20" width="20" fillColor="#000" />
                                                </span>
                                            </GlobalTooltip>
                                        </Grid>
                                    }

                                    {isAdmin(userDetail) && currentActiveChatbotThread && flagDebug &&
                                        <Grid item className={clsx(styles.relativePositonStyle, aiMessageExecutionActionType && styles.bugReportIconStyle)}>
                                            <GlobalTooltip
                                                title="View Execution Time Log"
                                                placement='top'
                                                arrow
                                            >
                                                <span className={styles.executionLogButtonStyle} onClick={() => handleExecutionTimeLog(thread)}>
                                                    <BugReportIcon />
                                                </span>
                                            </GlobalTooltip>
                                        </Grid>
                                    }

                                    <pre
                                        className={styles.botMsgBoxStyle}
                                        style={{
                                            color: user_msg_color,
                                        } as CSSProperties}
                                        dir="auto"
                                    >
                                        {/* Quote Reply */}
                                        {renderQuoteReply()}
                                        {renderUserMsg(thread, targetRef)}
                                    </pre>

                                    {actionExecutionData && "reaction_data" in actionExecutionData && reactionList && reactionList[0].reaction_emoji_character !== "" && reactionList.map((reaction: reactionData, reactionInd: number) => {
                                        return <React.Fragment key={reactionInd}>
                                            <p className={styles.reactionStyle}>{reaction.reaction_emoji_character}</p>
                                        </React.Fragment>
                                    }
                                    )}
                                    
                                    {/* Copy only for text messages (not media, voice call or template previews) */}
                                    {copyMessageText &&
                                        <CopyToClipboard text={stripMarkdown(copyMessageText)}>
                                            <GlobalTooltip
                                                title={copyText}
                                                arrow
                                                onMouseLeave={() => setCopyText("Copy!")}
                                                onClick={() => {
                                                    setCopyText("Copied!");
                                                }}
                                            >
                                                <Grid item className={styles.userCopyIcon}>
                                                    <IconsColor iconName={"Copy"}
                                                        fillColor={"black"}
                                                        width={"16"} height={"16"} />&nbsp;
                                                </Grid>
                                            </GlobalTooltip>
                                        </CopyToClipboard>
                                    }

                                    <Grid container gap={"3px"} alignItems={"center"} justifyContent={"end"} mt={0.5} sx={{ margin: '4px 0 2px 0' }}>
                                        <span className={styles.whatsAppStatusSpan} style={{ color: user_msg_color }}>{formatDateAccordingToTimezoneUsingMoment(thread.timestamp, userDetail.timezone_hour)}</span>
                                        {getMessageStatusIcons(getReleventWhatsappStatus(thread), thread, aiMessageExecutionActionType)}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>


            </Grid>
        </Grid>
    );
}

export default UserMessage;  
